import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { fmtMoney } from "../lib/format";
import { useAuth } from "../lib/auth";
import PositionMapAgent from "./PositionMapAgent";
import {
  Mail, MessageCircle, FileText, Globe, User, X, ChevronRight,
  Plus, Inbox as InboxIcon, AlertCircle, Eye, Minus,
} from "lucide-react";

const STATUS_META = {
  priority_needed: { label: "Priority needed", color: "#ef4444", ring: "ring-red-400", bg: "bg-red-500/15", text: "text-red-700" },
  evaluating:      { label: "Evaluating",       color: "#f59e0b", ring: "ring-amber-400", bg: "bg-amber-500/15", text: "text-amber-700" },
  covered:         { label: "Covered",          color: "#10b981", ring: "ring-emerald-400", bg: "bg-emerald-500/15", text: "text-emerald-700" },
  not_a_priority:  { label: "Not a priority",   color: "#64748b", ring: "ring-slate-300", bg: "bg-slate-200", text: "text-slate-600" },
};

const STATUS_ORDER = ["priority_needed", "evaluating", "covered", "not_a_priority"];

const CAND_STATUS_TONE = {
  active_target: "pm-pill-blue",
  offer_made:    "pm-pill-amber",
  deal_done:     "pm-pill-green",
  rejected:      "pm-pill-red",
  pending:       "pm-pill-slate",
};

const SOURCE_ICON = {
  email: Mail, whatsapp: MessageCircle, pdf: FileText,
  transfermarkt: Globe, manual: User,
};

const FORMATION_OPTIONS = ["4-3-3", "4-2-3-1", "3-5-2"];

export default function PositionMap() {
  const { user } = useAuth();
  // Agents and admins get the roster-grouping variant; only club-side
  // tactical roles see the formation window UI below.
  const isAgentSide = !user || ["agent", "associate", "admin"].includes(user.role);
  if (isAgentSide) return <PositionMapAgent />;
  return <ClubPositionMap />;
}

function ClubPositionMap() {
  const [data, setData] = useState(null);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [intakeAssignFor, setIntakeAssignFor] = useState(null); // intake obj
  const [draggingIntakeId, setDraggingIntakeId] = useState(null);
  const [dragOverSlotId, setDragOverSlotId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/position-map/window");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const selectedSlot = useMemo(() => {
    if (!data) return null;
    return data.slots.find((s) => s.id === selectedSlotId) || null;
  }, [data, selectedSlotId]);

  const slotCandidates = useMemo(() => {
    if (!data || !selectedSlot) return [];
    return (data.candidates_by_slot?.[selectedSlot.id] || []);
  }, [data, selectedSlot]);

  const onChangeFormation = async (formation) => {
    if (!data) return;
    setBusy(true);
    try {
      const { data: updated } = await api.patch(`/position-map/window/${data.window.id}`, { formation });
      setData(updated);
      setSelectedSlotId(null);
    } finally { setBusy(false); }
  };

  const onChangeSlotStatus = async (slot, status) => {
    setBusy(true);
    try {
      await api.patch(`/position-map/slots/${slot.id}`, { status });
      await load();
    } finally { setBusy(false); }
  };

  const onChangeBudgetCeiling = async (slot, val) => {
    setBusy(true);
    try {
      await api.patch(`/position-map/slots/${slot.id}`, { budget_ceiling_eur: val });
      await load();
    } finally { setBusy(false); }
  };

  const onAssignIntake = async (intake, slotId) => {
    setBusy(true);
    try {
      await api.post(`/position-map/intake/${intake.id}/assign`, { slot_id: slotId });
      setIntakeAssignFor(null);
      await load();
    } finally { setBusy(false); }
  };

  const onDismissIntake = async (intake) => {
    setBusy(true);
    try {
      await api.post(`/position-map/intake/${intake.id}/dismiss`);
      await load();
    } finally { setBusy(false); }
  };

  const onUpdateCandidate = async (cardId, body) => {
    setBusy(true);
    try {
      await api.patch(`/position-map/candidates/${cardId}`, body);
      await load();
      if (selectedCandidate?.id === cardId) {
        const updated = { ...selectedCandidate, ...body };
        setSelectedCandidate(updated);
      }
    } finally { setBusy(false); }
  };

  const onDropOnSlot = async (slot) => {
    if (!draggingIntakeId || !data) return;
    const intake = data.intakes.find((i) => i.id === draggingIntakeId);
    setDraggingIntakeId(null);
    setDragOverSlotId(null);
    if (intake) await onAssignIntake(intake, slot.id);
  };

  if (!data) {
    return (
      <Layout title="Transfer Window Position Map" subtitle="Module 9 · pitch view of needs vs candidates">
        <div className="text-slate-500" data-testid="position-map-loading">Loading window…</div>
      </Layout>
    );
  }

  const { window: win, slots, intakes, budget, formation_layout } = data;
  const layoutBySlotLabel = Object.fromEntries(formation_layout.map((c) => [c.slot, c]));

  return (
    <Layout
      title="Transfer Window Position Map"
      subtitle={`Module 9 · ${win.team_name} · ${win.name}`}
    >
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6" data-testid="position-map-root">
        {/* LEFT: pitch + slot grid */}
        <div className="space-y-4">
          {/* Toolbar */}
          <div className="pm-card p-4 flex flex-wrap items-center gap-4" data-testid="position-map-toolbar">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Formation</div>
              <div className="flex items-center gap-1 mt-1">
                {FORMATION_OPTIONS.map((f) => (
                  <button
                    key={f}
                    data-testid={`formation-btn-${f}`}
                    disabled={busy}
                    onClick={() => onChangeFormation(f)}
                    className={`px-3 h-9 rounded-lg text-sm font-bold tracking-tight border transition-all ${
                      win.formation === f
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                    }`}
                  >{f}</button>
                ))}
              </div>
            </div>
            <div className="h-10 w-px bg-slate-200" />
            <div className="flex flex-wrap gap-3">
              {STATUS_ORDER.map((k) => {
                const m = STATUS_META[k];
                const count = slots.filter((s) => s.status === k).length;
                return (
                  <div key={k} className="flex items-center gap-2 text-xs" data-testid={`legend-${k}`}>
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color }} />
                    <span className="text-slate-700 font-semibold">{m.label}</span>
                    <span className="font-mono-tab text-slate-400">{count}</span>
                  </div>
                );
              })}
            </div>
            <div className="ml-auto text-xs text-slate-500">
              Drag intake cards from the right onto a slot to assign.
            </div>
          </div>

          {/* Pitch */}
          <div className="pm-card p-4">
            <div
              className="relative w-full"
              style={{
                aspectRatio: "70 / 90",
                background:
                  "linear-gradient(180deg, #0e7a3b 0%, #0a6730 100%)",
                borderRadius: 14,
                overflow: "hidden",
              }}
              data-testid="position-map-pitch"
            >
              <PitchLines />
              {slots.map((s) => {
                const cell = layoutBySlotLabel[s.slot] || { x: s.x, y: s.y };
                const meta = STATUS_META[s.status] || STATUS_META.not_a_priority;
                const cands = data.candidates_by_slot?.[s.id] || [];
                const isSelected = selectedSlotId === s.id;
                const isDragOver = dragOverSlotId === s.id;
                return (
                  <button
                    key={s.id}
                    data-testid={`slot-${s.slot}`}
                    onClick={() => setSelectedSlotId(s.id)}
                    onDragOver={(e) => { if (draggingIntakeId) { e.preventDefault(); setDragOverSlotId(s.id); } }}
                    onDragLeave={() => setDragOverSlotId((cur) => cur === s.id ? null : cur)}
                    onDrop={() => onDropOnSlot(s)}
                    className={`absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center text-white transition-all ${
                      isSelected ? "scale-110 z-20" : "z-10"
                    } ${isDragOver ? "ring-4 ring-yellow-300 scale-110" : ""}`}
                    style={{
                      left: `${cell.x}%`,
                      top: `${cell.y}%`,
                      width: 70,
                      height: 70,
                    }}
                  >
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center font-display font-black text-sm shadow-lg border-2 border-white/30 ${
                        isSelected ? "ring-4 ring-white/60" : ""
                      }`}
                      style={{ background: meta.color }}
                    >
                      {s.slot}
                    </div>
                    {cands.length > 0 && (
                      <div className="mt-1 flex items-center gap-1 bg-black/55 backdrop-blur-sm px-2 py-0.5 rounded-full">
                        <div className="flex -space-x-1.5">
                          {cands.slice(0, 3).map((c) => (
                            <div
                              key={c.id}
                              className="w-4 h-4 rounded-full bg-white/90 ring-1 ring-white overflow-hidden"
                              style={{
                                backgroundImage: c.photo_url ? `url(${c.photo_url})` : undefined,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                              }}
                            />
                          ))}
                        </div>
                        <span className="text-[10px] font-mono-tab text-white">×{cands.length}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="text-xs text-slate-500 mt-3" data-testid="pitch-help">
              Click any slot to manage candidates and status. Stacked dots = multiple candidates ranked by priority.
            </div>
          </div>

          {/* Budget tracker */}
          <BudgetCard window={win} budget={budget} onSetBudget={async (v) => {
            await api.patch(`/position-map/window/${win.id}`, { total_budget_eur: v });
            await load();
          }} />

          {/* Selected slot drawer */}
          {selectedSlot && (
            <SlotPanel
              slot={selectedSlot}
              candidates={slotCandidates}
              onClose={() => setSelectedSlotId(null)}
              onChangeStatus={(st) => onChangeSlotStatus(selectedSlot, st)}
              onChangeBudget={(v) => onChangeBudgetCeiling(selectedSlot, v)}
              onSelectCandidate={(c) => setSelectedCandidate(c)}
              onUpdateCandidate={onUpdateCandidate}
            />
          )}
        </div>

        {/* RIGHT: intake queue */}
        <IntakeQueue
          intakes={intakes}
          slots={slots}
          onDragStart={(id) => setDraggingIntakeId(id)}
          onDragEnd={() => { setDraggingIntakeId(null); setDragOverSlotId(null); }}
          onAssign={(intake) => setIntakeAssignFor(intake)}
          onDismiss={onDismissIntake}
        />
      </div>

      {/* Modals */}
      {intakeAssignFor && (
        <AssignIntakeModal
          intake={intakeAssignFor}
          slots={slots}
          onClose={() => setIntakeAssignFor(null)}
          onAssign={(slotId) => onAssignIntake(intakeAssignFor, slotId)}
        />
      )}
      {selectedCandidate && (
        <CandidateModal
          candidate={selectedCandidate}
          onClose={() => setSelectedCandidate(null)}
          onUpdate={(body) => onUpdateCandidate(selectedCandidate.id, body)}
        />
      )}
    </Layout>
  );
}

/* -------------------- pitch graphics -------------------- */
function PitchLines() {
  return (
    <svg viewBox="0 0 100 130" preserveAspectRatio="none" className="absolute inset-0 w-full h-full opacity-60">
      <defs>
        <pattern id="grass" width="6" height="6" patternUnits="userSpaceOnUse">
          <rect width="6" height="6" fill="transparent" />
          <rect x="0" y="0" width="3" height="6" fill="rgba(255,255,255,0.025)" />
        </pattern>
      </defs>
      <rect width="100" height="130" fill="url(#grass)" />
      <rect x="2" y="2" width="96" height="126" fill="none" stroke="white" strokeWidth="0.4" />
      <line x1="2" y1="65" x2="98" y2="65" stroke="white" strokeWidth="0.4" />
      <circle cx="50" cy="65" r="9" fill="none" stroke="white" strokeWidth="0.4" />
      <circle cx="50" cy="65" r="0.6" fill="white" />
      {/* boxes */}
      <rect x="22" y="2" width="56" height="14" fill="none" stroke="white" strokeWidth="0.4" />
      <rect x="36" y="2" width="28" height="6" fill="none" stroke="white" strokeWidth="0.4" />
      <rect x="22" y="114" width="56" height="14" fill="none" stroke="white" strokeWidth="0.4" />
      <rect x="36" y="122" width="28" height="6" fill="none" stroke="white" strokeWidth="0.4" />
    </svg>
  );
}

/* -------------------- budget card -------------------- */
function BudgetCard({ window: win, budget, onSetBudget }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(budget?.total ?? "");
  useEffect(() => { setV(budget?.total ?? ""); }, [budget?.total]);

  const total = budget?.total || 0;
  const committed = budget?.committed || 0;
  const explored = budget?.explored || 0;
  const remaining = budget?.remaining ?? (total - committed);
  const committedPct = total ? Math.min(100, (committed / total) * 100) : 0;
  const exploredPct = total ? Math.min(100 - committedPct, (explored / total) * 100) : 0;

  return (
    <div className="pm-card p-5" data-testid="budget-tracker">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Window budget</div>
          <div className="font-display text-2xl font-black tracking-tight">
            {fmtMoney(total)}{" "}
            <button
              className="text-xs font-bold text-emerald-700 underline ml-2"
              onClick={() => setEditing((e) => !e)}
              data-testid="budget-edit-btn"
            >{editing ? "Cancel" : "Edit"}</button>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Remaining</div>
          <div className={`font-display text-xl font-black tracking-tight ${remaining < 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="budget-remaining">
            {fmtMoney(remaining)}
          </div>
        </div>
      </div>
      {editing && (
        <div className="flex items-center gap-2 mb-3">
          <input
            type="number"
            className="pm-input flex-1"
            value={v}
            onChange={(e) => setV(e.target.value)}
            data-testid="budget-input"
          />
          <button
            className="pm-btn-primary h-10 px-4 rounded-xl text-sm font-bold"
            data-testid="budget-save-btn"
            onClick={() => { onSetBudget(Number(v) || 0); setEditing(false); }}
          >Save</button>
        </div>
      )}
      <div className="h-3 rounded-full bg-slate-100 overflow-hidden flex">
        <div className="h-full bg-amber-500" style={{ width: `${committedPct}%` }} title={`Committed ${fmtMoney(committed)}`} />
        <div className="h-full bg-blue-400" style={{ width: `${exploredPct}%` }} title={`Exploring ${fmtMoney(explored)}`} />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <Stat label="Committed" value={fmtMoney(committed)} hint="offer / done" tone="amber" testid="budget-committed" />
        <Stat label="Exploring" value={fmtMoney(explored)} hint="active targets" tone="blue" testid="budget-explored" />
        <Stat label="Total" value={fmtMoney(total)} hint="window cap" tone="slate" testid="budget-total" />
      </div>
    </div>
  );
}

function Stat({ label, value, hint, tone, testid }) {
  const cls = tone === "amber" ? "border-amber-200 bg-amber-50"
    : tone === "blue" ? "border-blue-200 bg-blue-50"
    : "border-slate-200 bg-slate-50";
  return (
    <div className={`rounded-xl border ${cls} p-2.5`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{label}</div>
      <div className="font-display text-base font-black tracking-tight">{value}</div>
      <div className="text-[10px] text-slate-500">{hint}</div>
    </div>
  );
}

/* -------------------- slot panel -------------------- */
function SlotPanel({ slot, candidates, onClose, onChangeStatus, onChangeBudget, onSelectCandidate, onUpdateCandidate }) {
  const meta = STATUS_META[slot.status] || STATUS_META.not_a_priority;
  const [budgetV, setBudgetV] = useState(slot.budget_ceiling_eur ?? "");
  useEffect(() => { setBudgetV(slot.budget_ceiling_eur ?? ""); }, [slot.id, slot.budget_ceiling_eur]);

  const movePriority = async (c, dir) => {
    await onUpdateCandidate(c.id, { priority_rank: Math.max(1, (c.priority_rank || 1) + dir) });
  };

  return (
    <div className="pm-card p-5" data-testid={`slot-panel-${slot.slot}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Slot</div>
          <h3 className="font-display text-2xl font-black tracking-tight flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-sm font-black" style={{ background: meta.color }}>
              {slot.slot}
            </span>
            {meta.label}
          </h3>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="slot-panel-close"><X size={18} /></button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Status</div>
          <div className="grid grid-cols-2 gap-1.5">
            {STATUS_ORDER.map((s) => {
              const m = STATUS_META[s];
              const active = slot.status === s;
              return (
                <button
                  key={s}
                  data-testid={`slot-status-${s}`}
                  onClick={() => onChangeStatus(s)}
                  className={`text-left px-3 h-10 rounded-lg text-xs font-bold border transition-all flex items-center gap-2 ${
                    active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                  }`}
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: m.color }} />
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Budget ceiling for this slot</div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              className="pm-input flex-1"
              placeholder="e.g. 25000000"
              value={budgetV ?? ""}
              onChange={(e) => setBudgetV(e.target.value)}
              data-testid="slot-budget-input"
            />
            <button
              className="pm-btn-primary h-10 px-4 rounded-xl text-sm font-bold"
              onClick={() => onChangeBudget(Number(budgetV) || null)}
              data-testid="slot-budget-save"
            >Save</button>
          </div>
          <div className="text-[11px] text-slate-500 mt-2">
            Current: <span className="font-mono-tab">{slot.budget_ceiling_eur ? fmtMoney(slot.budget_ceiling_eur) : "—"}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Candidates ({candidates.length})</div>
      </div>
      {candidates.length === 0 && (
        <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-xl" data-testid="slot-no-candidates">
          No candidates yet. Drag an intake card or assign one from the queue.
        </div>
      )}
      <div className="space-y-2">
        {candidates.map((c, idx) => (
          <div
            key={c.id}
            className="border border-slate-200 rounded-xl p-3 flex items-center gap-3 bg-white hover:border-slate-400 transition-colors"
            data-testid={`candidate-card-${c.id}`}
          >
            <div className="flex flex-col gap-0.5">
              <button onClick={() => movePriority(c, -1)} className="text-slate-400 hover:text-slate-700" data-testid={`cand-up-${c.id}`}><Minus size={12} /></button>
              <span className="font-mono-tab text-xs font-bold text-slate-700 text-center">#{c.priority_rank}</span>
              <button onClick={() => movePriority(c, 1)} className="text-slate-400 hover:text-slate-700" data-testid={`cand-down-${c.id}`}><Plus size={12} /></button>
            </div>
            <div
              className="w-12 h-12 rounded-xl bg-slate-100 shrink-0"
              style={{
                backgroundImage: c.photo_url ? `url(${c.photo_url})` : undefined,
                backgroundSize: "cover", backgroundPosition: "center",
              }}
            />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm truncate flex items-center gap-2">
                {c.player_name}
                <span className={`pm-pill text-[10px] ${CAND_STATUS_TONE[c.status]}`}>{c.status.replace("_", " ")}</span>
              </div>
              <div className="text-xs text-slate-500 truncate">
                {c.position} · {c.age ? `${c.age}y` : ""} · {c.nationality || ""} · {c.current_club || ""}
              </div>
              <div className="text-[11px] font-mono-tab text-slate-700 mt-0.5">
                MV {fmtMoney(c.market_value_eur)} · Ask {fmtMoney(c.asking_fee_eur)}
              </div>
            </div>
            <button
              className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1"
              onClick={() => onSelectCandidate(c)}
              data-testid={`candidate-open-${c.id}`}
            >
              <Eye size={12} /> Open
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------- intake queue -------------------- */
function IntakeQueue({ intakes, slots, onDragStart, onDragEnd, onAssign, onDismiss }) {
  return (
    <div className="pm-card p-4 flex flex-col gap-3 h-fit xl:sticky xl:top-24" data-testid="intake-queue">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Pending intake</div>
          <h3 className="font-display text-xl font-black tracking-tight flex items-center gap-2">
            <InboxIcon size={18} /> Candidates ({intakes.length})
          </h3>
        </div>
        <span className="pm-pill pm-pill-amber text-[10px]" data-testid="intake-mocked">AI parsed</span>
      </div>
      {intakes.length === 0 && (
        <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-xl" data-testid="intake-empty">
          Nothing pending. Forward an email, WhatsApp, PDF or Transfermarkt URL to your aliases to populate this queue.
        </div>
      )}
      <div className="space-y-2 max-h-[60vh] overflow-y-auto pm-scroll">
        {intakes.map((i) => {
          const Icon = SOURCE_ICON[i.source] || User;
          return (
            <div
              key={i.id}
              draggable
              onDragStart={() => onDragStart(i.id)}
              onDragEnd={onDragEnd}
              className="border border-slate-200 rounded-xl p-3 bg-white hover:border-slate-400 transition-colors cursor-grab active:cursor-grabbing"
              data-testid={`intake-card-${i.id}`}
            >
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
                  <Icon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <div className="font-display font-bold text-sm truncate">{i.player_name || "Unknown"}</div>
                    <span className="pm-pill pm-pill-slate text-[10px]">{i.position || "—"}</span>
                    <span className="font-mono-tab text-[10px] text-slate-500">{Math.round((i.confidence || 0) * 100)}%</span>
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {i.nationality || ""} · {i.current_club || ""}
                  </div>
                  <div className="text-[11px] font-mono-tab text-slate-700 mt-0.5">
                    MV {fmtMoney(i.market_value_eur)} · Ask {fmtMoney(i.asking_fee_eur)}
                  </div>
                  <div className="text-[10px] text-slate-400 truncate mt-1">via {i.source} · {i.sender}</div>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1.5">
                <button
                  className="pm-btn-primary h-8 px-3 rounded-lg text-[11px] font-bold flex items-center gap-1"
                  onClick={() => onAssign(i)}
                  data-testid={`intake-assign-${i.id}`}
                ><ChevronRight size={11} /> Assign</button>
                <button
                  className="pm-btn-secondary h-8 px-3 rounded-lg text-[11px] font-bold flex items-center gap-1"
                  onClick={() => onDismiss(i)}
                  data-testid={`intake-dismiss-${i.id}`}
                ><X size={11} /> Dismiss</button>
                <span className="ml-auto text-[10px] text-slate-400">drag to pitch ↦</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------- modals -------------------- */
function AssignIntakeModal({ intake, slots, onClose, onAssign }) {
  const [sel, setSel] = useState(null);
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="assign-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Assign intake</div>
            <h3 className="font-display text-xl font-black">{intake.player_name}</h3>
            <div className="text-xs text-slate-500">{intake.position} · {intake.current_club}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Choose slot</div>
        <div className="grid grid-cols-3 gap-1.5 max-h-72 overflow-y-auto pm-scroll">
          {slots.map((s) => {
            const m = STATUS_META[s.status];
            const active = sel === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setSel(s.id)}
                className={`p-2 rounded-lg border text-xs font-bold flex items-center gap-2 transition-all ${
                  active ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`assign-slot-${s.slot}`}
              >
                <span className="w-2 h-2 rounded-full" style={{ background: m.color }} />
                {s.slot}
              </button>
            );
          })}
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold" onClick={onClose}>Cancel</button>
          <button
            className="pm-btn-primary h-10 px-4 rounded-xl text-sm font-bold disabled:opacity-50"
            disabled={!sel}
            onClick={() => onAssign(sel)}
            data-testid="assign-confirm-btn"
          >Place candidate</button>
        </div>
      </div>
    </div>
  );
}

function CandidateModal({ candidate, onClose, onUpdate }) {
  const c = candidate;
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="candidate-modal">
      <div className="bg-white rounded-2xl w-full max-w-xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-4 mb-4">
          <div
            className="w-16 h-16 rounded-xl bg-slate-100 shrink-0"
            style={{ backgroundImage: c.photo_url ? `url(${c.photo_url})` : undefined, backgroundSize: "cover", backgroundPosition: "center" }}
          />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Candidate</div>
            <h3 className="font-display text-2xl font-black">{c.player_name}</h3>
            <div className="text-sm text-slate-600">{c.position} · {c.age ? `${c.age}y` : ""} · {c.nationality} · {c.current_club}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm mb-4">
          <Field label="Market value">{fmtMoney(c.market_value_eur)}</Field>
          <Field label="Asking fee">{fmtMoney(c.asking_fee_eur)}</Field>
          <Field label="Contract ends">{c.contract_end || "—"}</Field>
          <Field label="Source">{c.source} {c.source_contact ? `· ${c.source_contact}` : ""}</Field>
          <Field label="Priority rank">#{c.priority_rank}</Field>
          <Field label="Status">
            <span className={`pm-pill text-[10px] ${CAND_STATUS_TONE[c.status]}`}>{c.status.replace("_", " ")}</span>
          </Field>
        </div>
        {c.transfermarkt_url && (
          <a href={c.transfermarkt_url} target="_blank" rel="noreferrer" className="text-xs font-bold text-emerald-700 underline flex items-center gap-1 mb-3">
            <Globe size={12} /> Open Transfermarkt profile
          </a>
        )}
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1.5">Change status</div>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {Object.keys(CAND_STATUS_TONE).map((st) => (
            <button
              key={st}
              onClick={() => onUpdate({ status: st })}
              data-testid={`cand-status-${st}`}
              className={`pm-pill text-[10px] cursor-pointer ${st === c.status ? "ring-2 ring-slate-900" : ""} ${CAND_STATUS_TONE[st]}`}
            >{st.replace("_", " ")}</button>
          ))}
        </div>
        <div className="text-xs text-slate-500 flex items-start gap-2">
          <AlertCircle size={12} className="mt-0.5 shrink-0 text-amber-500" />
          Move between status to reflect negotiation: <b>active_target → offer_made → deal_done</b>. Rejected hides from active funnel.
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="border border-slate-200 rounded-lg p-2.5 bg-slate-50/50">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{label}</div>
      <div className="font-display font-bold text-slate-900 mt-0.5">{children}</div>
    </div>
  );
}
